import clsx from 'clsx'
import compact from 'lodash/compact'
import includes from 'lodash/includes'
import noop from 'lodash/noop'
import orderBy from 'lodash/orderBy'
import split from 'lodash/split'
import NextImage, { ImageProps } from 'next/image'
import NextLegacyImage, { ImageLoaderProps as ImageLegacyLoaderProps, ImageProps as ImageLegacyProps } from 'next/legacy/image'
import React, { CSSProperties, FC, useCallback, useMemo } from 'react'

import { PagePresentation, PageType } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image as ImageData, ImagePin as ImagePinData } from '@Globals/Image'
import { stringToPageType } from '@Globals/Page/Mappers'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './Image.module.scss'

export interface ImageOptions {
  path: string
}

type ImagePropsLegacy = {
  options: ImageOptions
} & Omit<React.JSX.IntrinsicElements['img'], 'src'>

export const Image: FC<ImagePropsLegacy> = (
  {
    alt = '',
    options: { path },
    ...rest
  },
) => (
  <img
    alt={alt}
    src={path}
    {...rest}
  />
)

interface ContentstackLoaderProps extends Omit<ImageLegacyLoaderProps, 'quality'> {
  cropRatio?: string
  objectFit?: ImageLegacyProps['objectFit']
  objectPosition?: ImageLegacyProps['objectPosition']
  quality?: number | string
}

export const contentstackLoader = (
  {
    cropRatio,
    objectFit,
    objectPosition = 'center top',
    quality = '75,90',
    src,
    width,
  }: ContentstackLoaderProps,
) => {
  if (!includes(src, 'contentstack')) {
    return src
  }

  const url = new URL(src)

  url.searchParams.set('format', 'pjpg')
  url.searchParams.set('auto', 'webp')

  if (quality) {
    url.searchParams.set('quality', quality.toString())
  }

  if (width) {
    url.searchParams.set('width', width.toString())
  }

  if (objectFit === 'cover' && cropRatio) {
    const offset = {
      bottom: '100',
      center: '50',
      left: '0',
      right: '100',
      top: '0',
    }

    const [offsetX, offsetY] = split(objectPosition.toString(), ' ')

    const cropParam = `${cropRatio},offset-x${offset[offsetX]},offset-y${offset[offsetY]}`

    url.searchParams.set('crop', cropParam)
  }

  return url.toString()
}

const generateSrcSet = ({
  width,
  ...props
}: ContentstackLoaderProps) => [
  contentstackLoader({ width, ...props }),
  ', ',
  contentstackLoader({ width: width * 2, ...props }),
  ' 2x',
].join('')

export const generateProductLinkingDataImage = (src: string): string => contentstackLoader({
  src,
  width: 250,
})

interface ImageV2Props extends ImageLegacyProps {
  cropRatio?: string
}

export const ImageV2: FC<ImageV2Props> = (props) => {
  const {
    cropRatio,
    objectFit,
    objectPosition,
    src,
    ...rest
  } = props

  if (!src) {
    console.error('invalid src for ImageV2')
    return null
  }

  return (
    <NextLegacyImage
      loader={params => contentstackLoader({
        ...params,
        cropRatio,
        objectFit,
        objectPosition,
      })}
      objectFit={objectFit}
      src={src}
      {...rest}
    />
  )
}

interface ImageV3Props extends ImageProps {
  className?: string
  cropRatio?: string
}

export const ImageV3: FC<ImageV3Props> = ({
  className,
  cropRatio,
  src,
  style,
  ...rest
}) => {
  if (!src) {
    console.error('invalid src for ImageV3')
    return null
  }

  return (
    <NextImage
      className={className}
      loader={
        params => contentstackLoader({
          ...params,
          cropRatio,
          objectFit: style?.objectFit,
          objectPosition: style?.objectPosition,
        })
      }
      src={src}
      style={style}
      {...rest}
    />
  )
}

type ImageV4Props = Omit<React.JSX.IntrinsicElements['img'], 'src' | 'width'> & {
  quality?: number
  src: string
  width: number
}

export const ImageV4: FC<ImageV4Props> = ({
  alt,
  onLoad = noop,
  quality,
  src,
  style,
  width,
  ...rest
}) => (
  <img
    alt={alt}
    onLoad={onLoad}
    src={contentstackLoader({
      quality,
      src,
      width: width * 2,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    srcSet={generateSrcSet({
      quality,
      src,
      width,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    style={style}
    {...rest}
  />
)

type IMGStyleProps = NonNullable<React.JSX.IntrinsicElements['img']['style']>

/** number is converted to pixels (px), and string is parsed as viewport width (vw) */
type PixelsOrViewportWidth = number | string

type Breakpoint = [
  /** min-width breakpoint */
  breakpoint: number,
  /** max container width before next breakpoint */
  width: PixelsOrViewportWidth,
  /** max container height */
  height?: PixelsOrViewportWidth,
]

export type PictureV2Props = Omit<React.JSX.IntrinsicElements['img'], 'priority'> & {
  breakpoints: Breakpoint[]
  /** Only applies to desktop */
  cropOnMobile?: boolean
  cropRatio?: string
  desktop: ImageData
  mobile?: ImageData
  objectFit?: IMGStyleProps['objectFit']
  objectPosition?: IMGStyleProps['objectPosition']
  priority?: boolean
}

const calculateBreakpointWidth = (breakpoint: number, width: PixelsOrViewportWidth): number => {
  if (typeof width === 'number') {
    return width
  }

  return (breakpoint / 100) * parseInt(width, 10)
}

const ImagePin = ({ className, id, type, x, y }: ImagePinData & { className?: string }) => {
  const openModal = useCMSModalOpen()

  const onClick = useCallback(() => {
    openModal(stringToPageType(type) as PageType, id, undefined, PagePresentation.Sidebar)
  }, [id, type, openModal])

  return (
    <div
      aria-label="Pin"
      className={clsx(className, styles.pin)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        onClick()
      }}
      role="button"
      style={{ '--x': x, '--y': y } as CSSProperties}
      tabIndex={0}
    />
  )
}

const ImagePins = ({
  desktopPins,
  mobilePins,
}: {
  desktopPins?: ImagePinData[] | null
  mobilePins?: ImagePinData[] | null
}) => {
  if (!useSplitTest('image_pins_v1', 'b')) {
    return null
  }

  if (
    (!desktopPins || desktopPins.length === 0)
    && (!mobilePins || mobilePins.length === 0)
  ) {
    return null
  }

  return (
    <div className={styles.pins}>
      {desktopPins?.map(pin => <ImagePin key={`desktop-pin-${pin.x}-${pin.y}`} className={styles.desktop} {...pin} />)}
      {mobilePins?.map(pin => <ImagePin key={`mobile-pin-${pin.x}-${pin.y}`} className={styles.mobile} {...pin} />)}
    </div>
  )
}

export const PictureV2: FC<PictureV2Props> = ({
  alt = '',
  breakpoints,
  cropOnMobile,
  cropRatio,
  desktop,
  mobile,
  objectFit,
  objectPosition,
  priority = false,
  ...imgProps
}) => {
  const breakpointsSorted = useMemo(() => orderBy(breakpoints, [0], ['desc']), [breakpoints])

  const sources = useMemo(() => compact(breakpointsSorted.map(([breakpoint], index) => {
    const next = breakpointsSorted[index - 1]
    if (!next) {
      return undefined
    }
    const isDesktop = breakpoint >= 768
    const [nextBreakpoint, nextWidth] = next
    const width = calculateBreakpointWidth(nextBreakpoint, nextWidth)
    let src = desktop.url
    if (!isDesktop && mobile) {
      src = mobile.url
    }
    const srcSet = generateSrcSet({
      cropRatio: isDesktop || cropOnMobile ? cropRatio : undefined,
      objectFit,
      objectPosition,
      src,
      width,
    })
    return ({ breakpoint, srcSet })
  })), [breakpointsSorted, cropOnMobile, cropRatio, desktop.url, mobile, objectFit, objectPosition])

  return (
    <span
      className={styles.image}
      style={{
        '--desktopHeight': `${desktop.dimensions.height}`,
        '--desktopWidth': `${desktop.dimensions.width}`,
        '--mobileHeight': mobile?.dimensions.height ? `${mobile?.dimensions.height}` : `${desktop.dimensions.height}`,
        '--mobileWidth': mobile?.dimensions.width ? `${mobile?.dimensions.width}` : `${desktop.dimensions.width}`,
      } as CSSProperties}
    >
      <span className={styles.padder} />
      <picture>
        {sources.map(({
          breakpoint,
          srcSet,
        }) => (
          <source
            key={breakpoint}
            media={`(min-width: ${breakpoint}px)`}
            srcSet={srcSet}
          />
        ))}
        <img
          alt={alt}
          fetchPriority={priority ? 'high' : 'auto'}
          srcSet={generateSrcSet({
            src: mobile?.url || desktop.url,
            width: 320,
          })}
          style={{
            objectFit,
            objectPosition,
          } as CSSProperties}
          {...imgProps}
        />
      </picture>
      <ImagePins desktopPins={desktop?.pins} mobilePins={mobile?.pins} />
    </span>
  )
}

